import { initializeApp } from "firebase/app";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  getAuth,
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
  getIdToken,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  serverTimestamp
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";

require('firebase/auth');

const firebaseConfig = {
  apiKey: "AIzaSyD7nkfklqqqYRlI1uZmc2RUCzkSosmSTlc",
  authDomain: "tripdio.firebaseapp.com",
  projectId: "tripdio",
  storageBucket: "tripdio.appspot.com",
  messagingSenderId: "525239729222",
  appId: "1:525239729222:web:a970a40215bcc27f415e47",
  measurementId: "G-B2K1Z99Y5Y"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app);

export { app, db, auth, storage, serverTimestamp };

// Ownership verification of Google hotels (functions/index.js). Each takes { placeId, ... }.
const callable = (name) => async (data) => (await httpsCallable(functions, name)(data)).data;
export const getClaimOptions = callable("getClaimOptions");
export const startClaimVerification = callable("startClaimVerification");
export const confirmClaimVerification = callable("confirmClaimVerification");


export const getToken = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      if (user) {
        getIdToken(user).then(
          (idToken) => {
            resolve(idToken);
          },
          (error) => {
            resolve(null);
          }
        );
      } else {
        resolve(null);
      }
    });
  });
};

export const token = async () => {
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const idToken = await currentUser.getIdToken();
      return idToken;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error retrieving ID token:", error);
    return null;
  }
};

export const signInUser = (email, password) => {
  const auth = getAuth();
  return setPersistence(auth, browserLocalPersistence)
    .then(() => {
      return signInWithEmailAndPassword(auth, email, password);
    })
    .catch((error) => {
      const errorCode = error.code;
      const errorMessage = error.message;
      console.error(errorCode, errorMessage);
      throw error;
    });
};

// Default avatar for accounts without a photo.
const DEFAULT_PROFILE_IMAGE =
  "https://firebasestorage.googleapis.com/v0/b/tripdio.appspot.com/o/user.png?alt=media&token=0ab01826-e0d2-430b-8dfd-3cab3070aa34";

/**
 * Signs in with Google. The profile is not created here: App sends accounts
 * without a complete profile to /complete-profile (see isProfileComplete).
 */
export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  await setPersistence(auth, browserLocalPersistence);
  return signInWithPopup(auth, provider);
};

/** A profile has what the registration form asks for. */
export const isProfileComplete = (profile) =>
  Boolean(profile?.displayName?.trim() && profile?.phone?.trim() && profile?.point?.trim());

/** Saves the registration fields for the signed-in user (used after signing in with Google). */
export const completeProfile = async ({ displayName, phone, point, location }) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Tu sesión expiró. Volvé a iniciar sesión.");
  const userRef = doc(db, "users", user.uid);
  const existing = await getDoc(userRef);
  await setDoc(
    userRef,
    {
      uid: user.uid,
      email: user.email,
      displayName,
      phone,
      point,
      location,
      // Keep a photo the user already chose; otherwise use the Google one.
      ...(!existing.data()?.profileImageUrl && {
        profileImageUrl: user.photoURL || DEFAULT_PROFILE_IMAGE,
      }),
    },
    { merge: true }
  );
};

export const resetPassword = async (email) => {
  const normalizedEmail = email.trim().toLowerCase();

  if (!normalizedEmail) {
    throw new Error("Ingresa un correo válido.");
  }

  try {
    await sendPasswordResetEmail(auth, normalizedEmail);
    console.log(
      "¡El correo electrónico para restablecer la contraseña se envió correctamente!"
    );
  } catch (error) {
    if (error.code === "auth/invalid-email") {
      throw new Error("El formato del correo no es válido.");
    }

    if (error.code === "auth/too-many-requests") {
      throw new Error("Demasiados intentos. Espera unos minutos e intenta de nuevo.");
    }

    if (error.code === "auth/user-not-found") {
      throw new Error("No existe ninguna cuenta registrada con ese correo.");
    }

    if (error.code === "auth/network-request-failed") {
      throw new Error("Sin conexión o red inestable. Intenta nuevamente.");
    }

    throw new Error(error.message || "No se pudo enviar el correo de recuperación.");
  }
};

// True between creating an email account and saving its profile, so App doesn't
// mistake the new account for an incomplete one and flash /complete-profile.
let registering = false;
export const isRegistering = () => registering;

export const createUser = async (
  email,
  password,
  displayName,
  phone,
  point,
  location = null, // { locality, state, country, countryCode }
) => {
  registering = true;
  try {
    const auth = getAuth();
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    const user = userCredential.user;
    const uid = user.uid;

    const db = getFirestore();
    const userRef = doc(db, "users", uid);
    await setDoc(userRef, {
      uid: uid,
      email: email,
      displayName: displayName,
      phone: phone,
      point: point,
      location: location,
      profileImageUrl: DEFAULT_PROFILE_IMAGE,
    });

    return userCredential;
  } catch (error) {
    throw error;
  } finally {
    registering = false;
  }
};

export const updateUser = async (uid, displayName, phone, point) => {
  try {
    const db = getFirestore(app);
    const userRef = doc(db, "users", uid);
    await updateDoc(userRef, {
      displayName: displayName,
      phone: phone,
      point: point,
    });
    const userData = await getDoc(userRef);
    if (userData.exists()) {
      return { uid, ...userData.data() };
    } else {
      throw new Error("El usuario no existe");
    }
  } catch (error) {
    throw error;
  }
};

export const uploadImage = async (file) => {
  try {
    // storage.rules only accept uploads under the uploader's uid, as images, without overwriting.
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Tenés que iniciar sesión para subir imágenes.");
    const extension = (file.name.split(".").pop() || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const storageRef = ref(storage, `images/${uid}/${crypto.randomUUID()}${extension ? `.${extension}` : ""}`);
    await uploadBytes(storageRef, file, { contentType: file.type }); // Sube el archivo
    const downloadURL = await getDownloadURL(storageRef); // Obtiene la URL de descarga del archivo
    return downloadURL;
  } catch (error) {
    throw error;
  }
};

export const updateUserProfile = async (uid, profileData) => {
  try {
    const userRef = doc(db, "users", uid);
    await updateDoc(userRef, profileData);
    console.log("Perfil del usuario actualizado correctamente.");
  } catch (error) {
    console.error("Error al actualizar el perfil del usuario:", error);
    throw error;
  }
};